import { ActionItem, CampaignOption, ChangeRow } from './types';

const DAY = 86_400_000;

// Card money is in the store's currency.
// A bare '$' was wrong for CA, AU and UK.
export function currencyPrefix(store: string | null): string {
  if (store === 'CA') return 'CA$';
  if (store === 'AU') return 'A$';
  if (store === 'UK') return '£';
  return '$';
}

/** Prefix for an item's magnitude: the card's own stamp, or the store's currency on cards stored before the stamp existed. */
export function itemCurrency(item: Pick<ActionItem, 'store' | 'card'>): string {
  return item.card?.currency ?? currencyPrefix(item.store);
}

/** Collector magnitudes are weekly, in the card's currency; display is always monthly (same constant as the collector). */
export const WEEKS_PER_MONTH = 4.33;

/**
 * Snowflake JSON timestamps arrive zone-less in UTC ("2026-08-01 07:32:52.904").
 * Normalize to ISO and pin to UTC so age/urgency math isn't off by the local offset.
 */
export const parseTs = (value: string): Date =>
  new Date(value.includes(' ') ? `${value.replace(' ', 'T')}Z` : value);

/** Local calendar date (YYYY-MM-DD) for a UTC timestamp — for display. */
export const localDay = (value: string): string => parseTs(value).toLocaleDateString('en-CA');

// A calendar date string ('2026-08-20' or a longer timestamp starting with one) as "Aug 20".
// Parsed at local noon so the day never shifts across the UTC boundary the way new Date('YYYY-MM-DD') does.
export const shortDay = (value: string): string =>
  new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export function ageToken(iso: string, now: Date): string {
  const ms = now.getTime() - parseTs(iso).getTime();
  if (ms < 3_600_000) return `${Math.max(1, Math.round(ms / 60_000))}m`;
  if (ms < DAY) return `${Math.round(ms / 3_600_000)}h`;
  return `${Math.round(ms / DAY)}d`;
}

/** Whole days from today (local calendar) to a business date like due_on. */
export function daysUntil(dateStr: string, now: Date): number {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  const target = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / DAY);
}

export function urgencyToken(item: ActionItem, now: Date): { text: string; overdue: boolean } | null {
  if (!item.due_on) return null;
  const days = daysUntil(item.due_on, now);
  if (days < 0) return { text: `overdue ${-days}d`, overdue: true };
  return { text: days === 0 ? 'due today' : `due in ${days}d`, overdue: false };
}

/**
 * The change feed repeats itself: a single edit re-ingested by two sources, or a burst that did
 * not group, arrives as several rows with the same day and the same words. Keyed on that pair
 * rather than on change_key, which is exactly what differs between the copies.
 */
export function dedupeChanges(changes: ChangeRow[]): ChangeRow[] {
  const seen = new Set<string>();
  return changes.filter(change => {
    const key = `${localDay(change.changed_at)}|${change.summary.trim().toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ── Watchdog list ───────────────────────────────────────────────────────────

export type Brand = 'dosaze' | 'noonlash' | 'araluna';

// Mirror of sbly-dash-collector src/intelligence/config.ts BRAND_STORES. The collector
// schedule lives in kubernetes/watchdog-cronjob.yaml in that repo.
export const BRANDS: { key: Brand; label: string; stores: readonly string[] }[] = [
  { key: 'dosaze', label: 'Dosaze', stores: ['US', 'CA', 'AU', 'UK'] },
  { key: 'noonlash', label: 'Noonlash', stores: ['NOONLASH'] },
  { key: 'araluna', label: 'Araluna', stores: ['ARALUNA'] },
];

export const DEFAULT_BRAND: Brand = 'dosaze';

export function storesForBrand(brand: Brand, store?: string): string[] {
  const stores = BRANDS.find(candidate => candidate.key === brand)?.stores ?? [];
  return store && stores.includes(store) ? [store] : [...stores];
}

// ── My campaigns picker ─────────────────────────────────────────────────────

// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/ownershipGroups.ts LIVE_CAMPAIGN_DAYS.
export const LIVE_CAMPAIGN_DAYS = 30;

// A search word that names a store or a brand filters by store instead of matching names, so
// "us" finds US campaigns rather than every name containing "Australia".
const STORE_WORDS = new Map<string, readonly string[]>(BRANDS.flatMap(brand => [
  [brand.key, brand.stores] as const,
  ...brand.stores.map(store => [store.toLowerCase(), [store]] as const),
]));

export function matchCampaigns(campaigns: CampaignOption[], query: string): CampaignOption[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return campaigns.filter(campaign => words.every(word => {
    const stores = STORE_WORDS.get(word);
    return stores ? campaign.store != null && stores.includes(campaign.store) : campaign.campaignName.toLowerCase().includes(word);
  }));
}

export interface CampaignStoreGroup { store: string | null; label: string; campaigns: CampaignOption[] }

// Stores in brand-tab order, then any store the tabs don't know, then campaigns with no store.
// A store is named by its code under a multi-store brand (US, UK) and by the brand otherwise.
export function campaignStoreGroups(campaigns: CampaignOption[]): CampaignStoreGroup[] {
  const known = BRANDS.flatMap(brand => brand.stores.map(store => ({ store, label: brand.stores.length > 1 ? store : brand.label })));
  const rank = (store: string | null) => { const index = known.findIndex(entry => entry.store === store); return index === -1 ? (store == null ? known.length + 1 : known.length) : index; };
  const groups = new Map<string | null, CampaignOption[]>();
  for (const campaign of campaigns) groups.set(campaign.store, [...(groups.get(campaign.store) ?? []), campaign]);
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || String(a).localeCompare(String(b)))
    .map(([store, list]) => ({
      store,
      label: known.find(entry => entry.store === store)?.label ?? store ?? 'No store',
      campaigns: [...list].sort((a, b) => a.campaignName.localeCompare(b.campaignName)),
    }));
}

export function itemStores(item: Pick<ActionItem, 'store' | 'unit_keys'>): string[] | null {
  if (item.store != null) return [item.store];
  return item.unit_keys?.stores ?? null;
}

export function inScope(item: ActionItem, brand: Brand, store?: string): boolean {
  const stores = itemStores(item);
  if (stores == null) return true;
  const shown = storesForBrand(brand, store);
  return stores.some(candidate => shown.includes(candidate));
}

export const issueFamily = (key: string): string => key.split('\0')[0];

// Tracking is an operational finding that stays with the campaign list. It is not a
// system line because the brief reserves that line for these two store-wide families.
export const SYSTEM_FAMILIES = new Set(['data_freshness', 'watchdog_coverage']);

export function isSystemAlert(item: ActionItem): boolean {
  if (item.source !== 'watchdog' || item.unit_type !== 'store') return false;
  const lead = item.card?.issues?.[0];
  if (lead) return SYSTEM_FAMILIES.has(issueFamily(lead.key));
  const label = item.unit_label ?? '';
  return label === 'Watchdog checks' || label === item.store || label === `${item.store} store`;
}

export function hasUnmappedCampaign(item: ActionItem): boolean {
  return item.card?.issues?.some(issue => issueFamily(issue.key) === 'unmapped_campaign') ?? false;
}

export function isUnmappedOnly(item: ActionItem): boolean {
  const issues = item.card?.issues ?? [];
  return issues.length > 0 && issues.every(issue => issueFamily(issue.key) === 'unmapped_campaign');
}

// The rows the list shows and counts. System alerts sit in their own box and unmapped-only cards
// in the system line, so neither is a card.
export const isListCard = (item: ActionItem): boolean => !isSystemAlert(item) && !isUnmappedOnly(item);

export const sortWatchdogItems = (items: ActionItem[]): ActionItem[] => [...items].sort((a, b) => {
  const aCost = a.card?.costOfDelay?.perDayUsd;
  const bCost = b.card?.costOfDelay?.perDayUsd;
  if (aCost == null && bCost != null) return 1;
  if (aCost != null && bCost == null) return -1;
  if (aCost != null && bCost != null && bCost !== aCost) return bCost - aCost;
  return a.first_seen.localeCompare(b.first_seen);
});

export const TODAY_CAP = 7;

export type OwnerMode = 'anyone' | 'mine' | 'unclaimed';

export function applyOwner(items: ActionItem[], mode: OwnerMode, actor: string): ActionItem[] {
  if (mode === 'mine') return items.filter(item => item.claimed_by === actor);
  if (mode === 'unclaimed') return items.filter(item => item.claimed_by == null);
  return items;
}

export interface WatchdogQuery { brand: Brand; store?: string; owner?: OwnerMode }

const BRAND_KEYS = BRANDS.map(brand => brand.key);

export function parseWatchdogQuery(search: string): WatchdogQuery {
  const params = new URLSearchParams(search);
  const requestedBrand = params.get('brand') as Brand | null;
  const brand = requestedBrand && BRAND_KEYS.includes(requestedBrand) ? requestedBrand : DEFAULT_BRAND;
  const allowedStores = storesForBrand(brand);
  const requestedStore = params.get('store');
  const store = requestedStore && allowedStores.length > 1 && allowedStores.includes(requestedStore)
    ? requestedStore
    : undefined;
  const owner = params.get('owner');
  return {
    brand,
    store,
    owner: owner === 'mine' || owner === 'unclaimed' ? owner : undefined,
  };
}

export function serializeWatchdogQuery(query: WatchdogQuery): string {
  const params = new URLSearchParams();
  if (query.brand !== DEFAULT_BRAND) params.set('brand', query.brand);
  if (query.store) params.set('store', query.store);
  if (query.owner && query.owner !== 'anyone') params.set('owner', query.owner);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}
